import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Column as ColumnType, api } from '../../api/client';
import { Card } from './Card';
import { useBoardStore } from '../../stores/boardStore';
import { useToastStore } from '../../stores/toastStore';
import { Modal } from '../common/Modal';

interface ColumnProps {
  column: ColumnType;
}

const MARKDOWN_EXTENSIONS = ['.md', '.markdown'];
const DOCUMENT_EXTENSIONS = ['.pdf', '.docx', '.doc', '.xlsx', '.xls', '.csv'];
const FILE_TYPE_LABELS: Record<string, string> = {
  pdf: 'PDF',
  docx: 'Word Document',
  doc: 'Word Document',
  xlsx: 'Excel Spreadsheet',
  xls: 'Excel Spreadsheet',
  csv: 'CSV Spreadsheet',
};
const COLUMN_COLOR_OPTIONS = [
  '#DBEAFE',
  '#FEF3C7',
  '#D1FAE5',
  '#FCE7F3',
  '#EDE9FE',
  '#FDE68A',
  '#E5E7EB',
];

function getFileExtension(filename: string): string {
  const match = filename.match(/\.([^.]+)$/);
  return match ? match[1].toLowerCase() : '';
}

export function Column({ column }: ColumnProps) {
  const {
    board,
    currentUser,
    createCardFromMarkdown,
    createCard,
    refreshBoard,
    updateColumn,
    reorderColumn,
    deleteColumn,
  } = useBoardStore();

  type SortMode = 'position' | 'alpha' | 'newest' | 'oldest';
  const [sortMode, setSortMode] = useState<SortMode>(() => {
    const saved = localStorage.getItem(`docanban-sort-${column.id}`);
    return (saved as SortMode) || 'position';
  });
  const [isFileOver, setIsFileOver] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editedName, setEditedName] = useState(column.name);
  const [editedColor, setEditedColor] = useState(column.color);
  const [destinationColumnId, setDestinationColumnId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [columnError, setColumnError] = useState('');
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    data: {
      type: 'column',
      column,
    },
  });

  const handleSortChange = useCallback((mode: SortMode) => {
    setSortMode(mode);
    localStorage.setItem(`docanban-sort-${column.id}`, mode);
  }, [column.id]);

  const sortedCards = useMemo(() => {
    if (sortMode === 'position') return column.cards;
    const cards = [...column.cards];
    switch (sortMode) {
      case 'alpha': return cards.sort((a, b) => a.title.localeCompare(b.title));
      case 'newest': return cards.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
      case 'oldest': return cards.sort((a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime());
      default: return cards;
    }
  }, [column.cards, sortMode]);

  const columnIndex = board?.columns.findIndex((item) => item.id === column.id) ?? -1;
  const canManageColumns = currentUser?.id === board?.owner_id;
  const canMoveLeft = canManageColumns && columnIndex > 0;
  const canMoveRight = canManageColumns && columnIndex !== -1 && columnIndex < (board?.columns.length || 0) - 1;
  const canDeleteColumn = canManageColumns && (board?.columns.length || 0) > 1;
  const destinationColumns = useMemo(
    () => (board?.columns || []).filter((item) => item.id !== column.id),
    [board?.columns, column.id]
  );

  const openEditModal = () => {
    setEditedName(column.name);
    setEditedColor(column.color);
    setColumnError('');
    setShowMenu(false);
    setShowEditModal(true);
  };

  const openDeleteModal = () => {
    setDestinationColumnId(destinationColumns[0]?.id || '');
    setColumnError('');
    setShowMenu(false);
    setShowDeleteModal(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes('Files')) {
      setIsFileOver(true);
    }
  };

  const handleDragLeave = () => {
    setIsFileOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsFileOver(false);

    const files = Array.from(e.dataTransfer.files);

    for (const file of files) {
      const isMarkdown = MARKDOWN_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));
      const isDocument = DOCUMENT_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));

      if (!isMarkdown && !isDocument) {
        continue;
      }

      try {
        if (isMarkdown) {
          await createCardFromMarkdown(file, column.id);
        } else {
          const ext = getFileExtension(file.name);
          const title = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
          const description = `${FILE_TYPE_LABELS[ext] || 'Document'} attachment`;

          const newCard = await createCard({
            title,
            description,
            column_id: column.id,
            content: `# ${title}\n\nThis document has an attached ${FILE_TYPE_LABELS[ext] || 'file'}.`,
          });

          await api.uploadAttachment(newCard.id, file);
          await refreshBoard();
        }
      } catch (error) {
        console.error('Failed to upload file:', error);
        useToastStore.getState().addToast({ type: 'error', message: `Failed to upload ${file.name}` });
      }
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editedName.trim()) {
      setColumnError('Column name is required.');
      return;
    }

    setIsSaving(true);
    setColumnError('');

    try {
      await updateColumn(column.id, {
        name: editedName.trim(),
        color: editedColor,
      });
      setShowEditModal(false);
    } catch (error) {
      setColumnError((error as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSubmit = async () => {
    if (!canDeleteColumn) {
      setColumnError('A board needs at least one column.');
      return;
    }

    if (column.cards.length > 0 && !destinationColumnId) {
      setColumnError('Pick a destination for the cards in this column.');
      return;
    }

    setIsDeleting(true);
    setColumnError('');

    try {
      await deleteColumn(column.id, column.cards.length > 0 ? destinationColumnId : undefined);
      setShowDeleteModal(false);
    } catch (error) {
      setColumnError((error as Error).message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMove = async (direction: 'left' | 'right') => {
    try {
      await reorderColumn(column.id, direction);
    } catch (error) {
      console.error('Failed to reorder column:', error);
    }
  };

  return (
    <>
      <div className="flex-shrink-0 w-[85vw] snap-center sm:w-80">
        <div
          className="relative rounded-t-xl px-4 py-3 mb-3 shadow-sm backdrop-blur-sm"
          style={{ backgroundColor: column.color }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="font-bold text-gray-800 text-base truncate">{column.name}</h2>
              <p className="mt-1 text-xs text-gray-600">
                {column.cards.length} {column.cards.length === 1 ? 'document' : 'documents'}
                {sortMode !== 'position' && (
                  <span className="ml-1.5 text-blue-600 font-medium">
                    · {sortMode === 'alpha' ? 'A→Z' : sortMode === 'newest' ? 'New↑' : 'Old↑'}
                  </span>
                )}
              </p>
            </div>

            <div className="flex items-center gap-1 shrink-0">
              {canManageColumns && (
                <>
                  <button
                    onClick={() => void handleMove('left')}
                    disabled={!canMoveLeft}
                    className="rounded-lg p-1.5 text-gray-600 hover:bg-white/60 disabled:opacity-35 disabled:cursor-not-allowed"
                    title="Move column left"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => void handleMove('right')}
                    disabled={!canMoveRight}
                    className="rounded-lg p-1.5 text-gray-600 hover:bg-white/60 disabled:opacity-35 disabled:cursor-not-allowed"
                    title="Move column right"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <button
                    ref={menuButtonRef}
                    onClick={() => setShowMenu((open) => !open)}
                    className="rounded-lg p-1.5 text-gray-600 hover:bg-white/60"
                    title="Column options"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6h.01M12 12h.01M12 18h.01" />
                    </svg>
                  </button>
                </>
              )}

              <span className="text-sm font-semibold text-gray-700 bg-white/70 backdrop-blur-sm px-2.5 py-1 rounded-full shadow-sm">
                {column.cards.length}
              </span>
            </div>
          </div>
        </div>

        <div
          ref={setNodeRef}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`space-y-3 min-h-[200px] p-3 rounded-xl transition-all duration-200 border-2 ${
            isOver
              ? 'bg-blue-50 border-blue-300 border-dashed shadow-inner'
              : isFileOver
                ? 'bg-blue-50 border-blue-400 border-dashed shadow-lg'
                : 'bg-gray-50/50 backdrop-blur-sm border-transparent'
          }`}
        >
          <SortableContext
            items={sortedCards.map((card) => card.id)}
            strategy={verticalListSortingStrategy}
          >
            {sortedCards.map((card) => (
              <Card key={card.id} card={card} />
            ))}
          </SortableContext>

          {column.cards.length === 0 && !isFileOver && (
            <div className="rounded-xl border border-dashed border-gray-200 bg-white/70 px-4 py-6 text-center text-sm text-gray-400">
              Drop files here, or add a new doc to start this lane.
            </div>
          )}

          {isFileOver && (
            <div className="py-10 flex flex-col items-center justify-center text-blue-600 pointer-events-none animate-pulse">
              <svg className="w-10 h-10 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-sm font-semibold">Drop Files to Import</span>
              <span className="text-xs text-blue-500 mt-1">MD, PDF, Word, Excel, CSV</span>
            </div>
          )}
        </div>
      </div>

      {showMenu && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setShowMenu(false)} />
          <div
            className="fixed z-[9999] w-48 rounded-xl border border-gray-200 bg-white py-1 shadow-xl"
            style={(() => {
              const rect = menuButtonRef.current?.getBoundingClientRect();
              if (!rect) return {};
              const menuHeight = 280;
              const top = rect.bottom + 8;
              const fitsBelow = top + menuHeight < window.innerHeight;
              return {
                top: fitsBelow ? top : rect.top - menuHeight - 8,
                left: Math.max(8, rect.right - 192),
              };
            })()}
          >
            <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Sort</div>
            {([
              ['position', 'Manual order'],
              ['alpha', 'A → Z'],
              ['newest', 'Newest first'],
              ['oldest', 'Oldest first'],
            ] as const).map(([mode, label]) => (
              <button
                key={mode}
                onClick={() => { handleSortChange(mode); setShowMenu(false); }}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm ${
                  sortMode === mode ? 'text-blue-600 font-medium bg-blue-50' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
            <div className="h-px bg-gray-100 my-1" />
            <button
              onClick={openEditModal}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Rename or recolor
            </button>
            <button
              onClick={openDeleteModal}
              disabled={!canDeleteColumn}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete column
            </button>
          </div>
        </>,
        document.body
      )}

      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} size="sm">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900">Edit Column</h3>
          <p className="mt-1 text-sm text-gray-500">
            Update the column label and header color.
          </p>

          <form onSubmit={handleEditSubmit} className="mt-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Column name</label>
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className="mt-2 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Header color</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {COLUMN_COLOR_OPTIONS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setEditedColor(color)}
                    className={`h-9 w-9 rounded-full border-2 transition-transform hover:scale-105 ${
                      editedColor === color ? 'border-slate-700' : 'border-white'
                    }`}
                    style={{ backgroundColor: color }}
                    aria-label={`Select column color ${color}`}
                  />
                ))}
              </div>
            </div>

            {columnError && <p className="text-sm text-red-600">{columnError}</p>}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="rounded-xl px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} size="sm">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900">Delete Column</h3>
          <p className="mt-1 text-sm text-gray-500">
            {column.cards.length > 0
              ? `This column has ${column.cards.length} ${column.cards.length === 1 ? 'document' : 'documents'}. Move them before the column is removed.`
              : 'This removes the column from the board.'}
          </p>

          {column.cards.length > 0 && (
            <div className="mt-5">
              <label className="block text-sm font-medium text-gray-700">Move cards to</label>
              <select
                value={destinationColumnId}
                onChange={(e) => setDestinationColumnId(e.target.value)}
                className="mt-2 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              >
                {destinationColumns.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!canDeleteColumn && (
            <p className="mt-4 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-700">
              You can’t delete the only column in a board.
            </p>
          )}

          {columnError && <p className="mt-4 text-sm text-red-600">{columnError}</p>}

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowDeleteModal(false)}
              className="rounded-xl px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleDeleteSubmit()}
              disabled={isDeleting || !canDeleteColumn}
              className="rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {isDeleting ? 'Deleting...' : 'Delete Column'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
